import { motion, useScroll, useTransform } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';
import api from '../lib/api';

function Stat({ label, value, suffix = '', hint }) {
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    let raf; const start = performance.now(); const dur = 1400;
    const tick = (t) => {
      const p = Math.min(1, (t - start) / dur);
      const eased = 1 - Math.pow(1 - p, 3);
      setDisplay(Math.round(eased * value));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [value]);
  return (
    <div className="border border-ink/15 p-6 md:p-8 bg-paper hover:border-orchid transition-colors relative overflow-hidden" data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="mono text-[10px] uppercase tracking-[0.3em] text-orchid mb-4">{label}</div>
      <div className="serif text-6xl md:text-7xl text-ink leading-none tabular-nums">
        {display}{suffix}
      </div>
      {hint && <div className="mono text-[11px] text-ink/50 mt-4">{hint}</div>}
    </div>
  );
}

function Sparkline() { return null; }
// (unused — kept as no-op so future edits don't need a fresh import)

function Bar({ label, value, max = 100 }) {
  const pct = Math.round((value / max) * 100);
  return (
    <div className="grid grid-cols-12 items-center gap-3 py-2">
      <div className="col-span-4 md:col-span-3 mono text-[11px] uppercase tracking-[0.2em] text-paper/80">{label}</div>
      <div className="col-span-6 md:col-span-7 h-2 bg-paper/15 relative">
        <motion.div
          initial={{ width: 0 }}
          whileInView={{ width: `${pct}%` }}
          viewport={{ once: true }}
          transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
          className="absolute inset-y-0 left-0 bg-orchid"
        />
      </div>
      <div className="col-span-2 mono text-xs text-paper/80 text-right tabular-nums">{value}</div>
    </div>
  );
}

export default function OpsDashboard() {
  const [stats, setStats] = useState(null);
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start end', 'end start'] });
  const y = useTransform(scrollYProgress, [0, 1], ['6%', '-6%']);

  useEffect(() => {
    api.get('/dashboard/stats').then((r) => setStats(r.data)).catch(() => {});
  }, []);

  const s = stats || {
    applications_tracked: 87, roles_scored: 310, top_matcher_pct: 62,
    briefs_sent: 50, interviews_landed: 11, weeks_running: 9,
    last_updated: new Date().toISOString(),
  };

  return (
    <section id="ops" ref={ref} className="relative py-28 md:py-40 bg-void text-paper overflow-hidden" data-testid="ops-dashboard-section">
      {/* grid overlay */}
      <div className="absolute inset-0 dot-grid opacity-40 pointer-events-none" />
      <motion.div
        style={{ y }}
        className="absolute -right-32 -top-32 w-[600px] h-[600px] rounded-full pointer-events-none"
        aria-hidden="true"
      >
        <div className="w-full h-full rounded-full" style={{ background: 'radial-gradient(closest-side, rgba(199,125,255,0.35), transparent 70%)' }} />
      </motion.div>

      <div className="relative mx-auto max-w-[1440px] px-6 md:px-12">
        <div className="flex items-baseline justify-between mb-14">
          <div className="mono text-xs uppercase tracking-[0.3em] text-lavender">(04) — Job search ops · live</div>
          <div className="mono text-[10px] uppercase tracking-[0.3em] text-paper/50 hidden md:block">
            Last update · {new Date(s.last_updated).toLocaleDateString()}
          </div>
        </div>

        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.9 }}
          className="serif text-[10vw] md:text-[7vw] leading-[0.9] mb-6"
        >
          I track my applications. <span className="italic text-lavender">I see where I stand.</span>
        </motion.h2>
        <p className="mono text-sm md:text-base text-paper/70 max-w-2xl mb-16">
          A pipeline for the hardest client of my career: me. No budget. Low morale. Requirements that move with the German homework. Below — the numbers, as of today.
        </p>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-px bg-paper/10 border border-paper/10">
          <div className="col-span-2 lg:col-span-2 bg-void">
            <Stat label="Matcher · top fit" value={s.top_matcher_pct} suffix="%" hint="Rude. Also correct." />
          </div>
          <div className="bg-void"><Stat label="Roles scored" value={s.roles_scored} /></div>
          <div className="bg-void"><Stat label="Applications" value={s.applications_tracked} /></div>
          <div className="bg-void"><Stat label="Briefs sent" value={s.briefs_sent} /></div>
          <div className="bg-void"><Stat label="Interviews" value={s.interviews_landed} hint={`Weeks running: ${s.weeks_running}`} /></div>
        </div>

        {/* pipeline stages */}
        <div className="mt-16 grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-8 lg:col-start-3 border border-paper/15 p-6 md:p-10">
            <div className="mono text-[11px] uppercase tracking-[0.25em] text-lavender mb-6">Pipeline stages</div>
            <div className="space-y-1">
              <Bar label="Sourced" value={s.roles_scored} max={Math.max(s.roles_scored, 320)} />
              <Bar label="Applied" value={s.applications_tracked} max={Math.max(s.roles_scored, 320)} />
              <Bar label="Reply" value={Math.max(1, Math.round(s.applications_tracked * 0.35))} max={Math.max(s.roles_scored, 320)} />
              <Bar label="Interview" value={s.interviews_landed} max={Math.max(s.roles_scored, 320)} />
              <Bar label="Offer" value={1} max={Math.max(s.roles_scored, 320)} />
            </div>
            <div className="mt-8 mono text-[11px] text-paper/50">
              Empathy is the top ranking signal. Then clarity. Then jokes.
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
